import { Card, Row, Col, Button, Form } from 'react-bootstrap';
import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import Select from 'react-select';
import FullPageSpinner from 'components/common/full-page-spinner';
import { toast } from 'react-toastify';

import {
  getWDSuppliers,
  getEVASuppliers
} from 'services/supplier/supplierDashboardService';

const SyncData = ({ onImportSuppliers }) => {
  const [wdSources, setWdSources] = useState([]);
  const [evaSources, setEvaSources] = useState([]);

  const [selectedWD, setSelectedWD] = useState([]);
  const [selectedEVA, setSelectedEVA] = useState([]);

  const [loadingWD, setLoadingWD] = useState(false);
  const [loadingEVA, setLoadingEVA] = useState(false);

  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    const controller = new AbortController();
    loadData(controller.signal);

    return () => {
      isMountedRef.current = false;
      controller.abort();
    };
  }, []);

  const loadData = async signal => {
    try {
      setLoadingWD(true);
      setLoadingEVA(true);

      const [wdRes, evaRes] = await Promise.all([
        getWDSuppliers(signal),
        getEVASuppliers(signal)
      ]);

      if (signal?.aborted) return;

      if (isMountedRef.current) {
        if (!wdRes.success) {
          toast.error(wdRes.message || 'Failed to load WD suppliers.');
        }
        if (!evaRes.success) {
          toast.error(evaRes.message || 'Failed to load EVA suppliers.');
        }

        setWdSources(
          wdRes?.data?.map(x => ({
            value: x.supplierCode + ";" + x.supplierId,
            label: `${x.supplierCode} - ${x.supplierName}`,
            source: 'WD'
          })) || []
        );

        setEvaSources(
          evaRes?.data?.map(x => ({
            value: x.supplierCode + ";" + x.supplierId,
            label: `${x.supplierCode} - ${x.supplierName}`,
            source: 'Eva'
          })) || []
        );
      }
    } catch (err) {
      if (signal?.aborted) return;
      console.error(err);
      toast.error('Failed to load supplier sources.');
    } finally {
      if (isMountedRef.current && !signal?.aborted) {
        setLoadingWD(false);
        setLoadingEVA(false);
      }
    }
  };

  return (
    <>
      <FullPageSpinner
        show={loadingEVA || loadingWD}
        quantity={3}
        variant="success"
      />
      <Card>
        <Card.Body className="bg-light">
          <Row className="g-4">
            {/* WD */}
            <Col md={6}>
              <Form.Label>
                <b>WD Source Suppliers</b>
              </Form.Label>
              <Select
                options={wdSources}
                isMulti
                isLoading={loadingWD}
                closeMenuOnSelect={false}
                placeholder="Select WD suppliers..."
                value={selectedWD}
                onChange={setSelectedWD}
              />

              <div className="mt-3 text-end">
                <Button
                  variant="primary"
                  onClick={async () => {
                    const success = await onImportSuppliers(selectedWD);
                    if (success) {
                      await loadData();
                      setSelectedWD([]);
                      setSelectedEVA([]);
                    }
                  }}
                  disabled={!selectedWD.length || loadingWD}
                >
                  Import New WD Suppliers
                </Button>
              </div>
            </Col>

            {/* EVA */}
            <Col md={6}>
              <Form.Label>
                <b>EVA Source Suppliers (Supplier Code)</b>
              </Form.Label>
              <Select
                options={evaSources}
                isMulti
                closeMenuOnSelect={false}
                isLoading={loadingEVA}
                placeholder="Select EVA suppliers..."
                value={selectedEVA}
                onChange={setSelectedEVA}
              />

              <div className="mt-3 text-end">
                <Button
                  variant="success"
                  onClick={async () => {
                    const success = await onImportSuppliers(selectedEVA);
                    if (success) {
                      await loadData();
                      setSelectedWD([]);
                      setSelectedEVA([]);
                    }
                  }}
                  disabled={!selectedEVA.length || loadingEVA}
                >
                  Import New EVA Suppliers
                </Button>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>
    </>
  );
};

SyncData.propTypes = {
  onImportSuppliers: PropTypes.func,
};

export default SyncData;
